import { useUserStore } from "@/store/user";
import { useEffect, useState } from "react";
import { BellIcon } from "@/components/Icons/BellIcon";
import { Button } from "@/components/ui/button";
import { NOTIFICATION_MAP } from "@/utils/mapping";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogClose } from "@/components/ui/dialog";
import { NotificationFilterType, NotificationType } from "@/types/user";
import { UserNotificationResults } from "../UserNotificationResults/UserNotificationResults";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem
} from "@/components/ui/select";
import { usePaginatedUserNotificationStore } from "@/store/paginated";
import { useIsMobile } from "@/hooks/useIsMobile";
import env from "@/env";

const NOTIFICATION_TYPES = [
  {
    label: "All",
    value: "all"
  },
  {
    label: "Device Related",
    value: "device"
  },
  {
    label: "Account Related",
    value: "cluster"
  }
];

export const UserNotificationsButton = () => {
  const [open, setOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const resultCount = usePaginatedUserNotificationStore((state) => state.resultCount);
  const markAllNotificatiosnAsRead = useUserStore((store) => store.markAllNotificatiosnAsRead);
  const [priority, setPriority] = useState<NotificationType | "all">("all");
  const [type, setType] = useState<NotificationFilterType>("all");
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const fetchResults = useUserStore((state) => state.fetchNotificationsRequest);
  const fetchAll = usePaginatedUserNotificationStore((state) => state.fetchResults);
  const reset = usePaginatedUserNotificationStore((state) => state.reset);
  const [isFull, setIsFull] = useState(false);
  const isMobile = useIsMobile();

  const handleMarkAllRead = async () => {
    setIsLoading(true);

    try {
      await markAllNotificatiosnAsRead();
      if (isFull) {
        await fetchAll();
      } else {
        reset();
      }
      setHasUnread(false);
      toast({ variant: "info", title: "All messages marked as read" });
    } catch (e) {
      toast({ variant: "error", title: e instanceof Error ? e.message : "Something went wrong" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      return;
    }
    const load = async () => {
      const response = await fetchResults({
        page: 1,
        limit: 5,
        seen: false
      });
      setHasUnread(!!response.results.length);
    };

    if (env.ENVIRONMENT) {
      load();
    }
    const interval = setInterval(load, 30000);
    return () => {
      clearInterval(interval);
    };
  }, [open, setHasUnread, fetchResults]);

  const triggerButton = (
    <Button
      data-testid={"UserNotificationsButton_trigger"}
      variant="outline"
      size="xs"
      className="relative gap-2"
      onClick={isMobile ? () => setOpen(true) : undefined}
    >
      <svg
        data-testid={"UserNotificationsButton_bellIcon"}
        className="size-[20px] shrink-0"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M9.99508 2.0957C6.29987 2.0957 4.69432 5.44174 4.69432 7.65364C4.69432 9.30682 4.93401 8.82032 4.01892 10.837C2.90147 13.7108 7.39512 14.8854 9.99508 14.8854C12.5943 14.8854 17.088 13.7108 15.9713 10.837C15.0563 8.82032 15.2959 9.30682 15.2959 7.65364C15.2959 5.44174 13.6896 2.0957 9.99508 2.0957Z"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M11.9203 17.0938C10.8417 18.2985 9.15922 18.3128 8.07031 17.0938"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span className="max-lg:hidden">Notifications</span>
      {resultCount && (
        <>
          <span className="max-lg:hidden relative flex items-center justify-center rounded-[6px] border border-border bg-background px-[6px] py-[2px] text-[12px] font-medium leading-[16px] text-foreground">
            {resultCount || "0"}
            <span className="absolute right-[-3px] top-[-3px] size-[10px] rounded-full border-[1.667px] border-background bg-red-500" />
          </span>
          <span className="lg:hidden absolute right-[-3px] top-[-3px] size-[10px] rounded-full border-[1.667px] border-background bg-red-500" />
        </>
      )}
    </Button>
  );

  const notificationsPanel = (isFull: boolean) => (
    <div className="flex flex-1 flex-col">
      <div
        className={`flex flex-col items-start gap-2 md:flex-row ${isFull ? "md:items-end" : "items-center"} flex-wrap border-b border-b-gray-dark-900 p-6`}
      >
        <div className={`flex gap-2 ${isFull ? "flex-col" : "flex-row items-center"}`}>
          {!isFull && <BellIcon className={`h-[16px] text-gray-dark-1000`} />}
          <span className={`${isFull ? "title-h5" : "label-small"} text-foreground`}>
            Notifications{" "}
            {!!resultCount && <span className="text-gray-dark-1000">({resultCount})</span>}
          </span>
          {isFull && (
            <p className="subtext-small text-gray-dark-1000">
              Be updated on your notifications all the time
            </p>
          )}
        </div>

        {!!resultCount && (
          <Button
            variant="link"
            onClick={handleMarkAllRead}
            className={cn(
              "subtext-small ml-auto h-auto gap-2 p-0 text-gray-dark-1000 underline",
              isLoading ? "pointer-events-none" : ""
            )}
          >
            <span>Mark All As Read</span>
          </Button>
        )}
        {isFull && (
          <div className="mt-4 flex w-full flex-col items-center gap-4 lg:flex-row">
            <div className="hidden w-full gap-1 rounded-lg border border-gray-dark-200 bg-gray-dark-1400 p-[1px] md:visible md:flex lg:w-fit">
              {NOTIFICATION_TYPES.map((item) => (
                <button
                  type="button"
                  onClick={() => {
                    setType(item.value as NotificationFilterType);
                  }}
                  key={`tab-${item.value}`}
                  className={cn(
                    "label-small inline-flex w-[125px] items-center justify-center whitespace-nowrap rounded-[8px] px-3 py-1",
                    type === item.value
                      ? "bg-container text-foreground"
                      : "text-gray-dark-1000"
                  )}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <Select
              onValueChange={(e: NotificationType) => setPriority(e)}
              value={priority}
              name="timezone"
            >
              <SelectTrigger className="h-[32px]">
                <SelectValue>
                  <div className="group-active flex flex-row items-center gap-2 text-gray-dark-1000">
                    <span>{(priority && NOTIFICATION_MAP[priority]?.label) || "All"}</span>
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="all">All</SelectItem>
                  {Object.values(NOTIFICATION_MAP).map((item) => {
                    return (
                      <SelectItem key={item.type} value={item.type}>
                        {item.label}
                      </SelectItem>
                    );
                  })}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div
        className={`custom-scrollbar ${isFull ? "max-h-[calc(100vh-100px-153px)]" : "max-h-[530px]"} overflow-y-auto overflow-x-hidden px-6 py-4 max-sm:px-4`}
      >
        <UserNotificationResults
          setIsFull={setIsFull}
          isFull={isFull}
          type={type}
          priority={priority}
        />
      </div>
      {!isFull && (
        <div className="border-t border-b-gray-dark-900 px-6 py-4">
          <Button onClick={() => setIsFull(true)} variant="outline" className="w-full">
            View All Notifications
          </Button>
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <>
        {triggerButton}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent
            showCloseIcon={false}
            className="flex h-[100dvh] max-h-none w-screen max-w-none flex-col gap-0 rounded-none border-0 p-0 translate-x-0 translate-y-0 top-0 left-0 data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-left-0 data-[state=closed]:slide-out-to-left-0 data-[state=open]:slide-in-from-top-0"
          >
            <div className="flex items-center justify-between border-b border-gray-dark-900 px-4 py-3">
              <div className="flex items-center gap-2">
                <BellIcon className="h-[16px] text-gray-dark-1000" />
                <span className="label-small text-foreground">
                  Notifications
                  {!!resultCount && (
                    <span className="ml-1 text-gray-dark-1000">({resultCount})</span>
                  )}
                </span>
              </div>
              <DialogClose asChild>
                <Button variant="ghost" size="xs" className="h-8 w-8 p-0">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="size-4"
                    aria-hidden="true"
                  >
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                  <span className="sr-only">Close</span>
                </Button>
              </DialogClose>
            </div>
            <div className="flex flex-1 flex-col overflow-hidden">
              {!!resultCount && (
                <div className="flex justify-end border-b border-gray-dark-900 px-4 py-2">
                  <Button
                    variant="link"
                    onClick={handleMarkAllRead}
                    className={cn(
                      "subtext-small h-auto gap-2 p-0 text-gray-dark-1000 underline",
                      isLoading ? "pointer-events-none" : ""
                    )}
                  >
                    <span>Mark All As Read</span>
                  </Button>
                </div>
              )}
              <div className="custom-scrollbar flex-1 overflow-y-auto overflow-x-hidden px-4 py-4">
                <UserNotificationResults
                  setIsFull={setIsFull}
                  isFull={true}
                  type={type}
                  priority={priority}
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
      }}
    >
      <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
      <PopoverContent
        className={`relative z-40 ${isFull ? "mt-[49px] min-h-[calc(100vh-100px)] w-[100vw] rounded-none sm:mr-[-92px] lg:w-[600px]" : "mt-3 min-h-[260px] w-[370px] rounded-[12px]"} flex flex-col border border-gray-dark-200 bg-background p-0`}
        align="end"
      >
        {notificationsPanel(isFull)}
      </PopoverContent>
    </Popover>
  );
};
